import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { featherAirplay } from '@ng-icons/feather-icons';
import { heroUsers, heroBars3, heroCheck } from '@ng-icons/heroicons/outline';
import { ionTimeOutline } from '@ng-icons/ionicons';
import { octBell } from '@ng-icons/octicons';
import { bootstrapBell } from '@ng-icons/bootstrap-icons';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NotificationsService } from 'src/app/api/services/notifications.service';
import { NotificationDto } from 'src/app/shared/model/notifications';
import { filter, finalize } from 'rxjs';
import { Profile } from 'src/app/shared/model/user';
import { UserService } from 'src/app/api/services';
import { TokenStorageService } from 'src/app/shared/service/token-storage.service';
import { UserSession } from 'src/app/shared/model/session';
import { APP_CONSTANTS } from 'src/app/core/constant/constants';
import { AuthService } from 'src/app/shared/service/auth.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { handleApiError } from 'src/app/shared/util/handle-api-error';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, NgIcon, RouterModule, LoadingButtonComponent],
  templateUrl: './header.component.html',
  viewProviders: [
    provideIcons({
      featherAirplay,
      ionTimeOutline,
      heroUsers,
      octBell,
      bootstrapBell,
      heroBars3,
      heroCheck,
    }),
  ],
})
export class HeaderComponent implements OnInit {
  userMenuOpen: boolean = false;
  mobileMenuOpen: boolean = false;
  emailMenuOpen: boolean = false;
  confirmLogoutOpen: boolean = false;
  isCompanyDashboard: boolean = false;
  notificationMenuOpen: boolean = false;
  loadingMarkRead: boolean = false;
  loadingLogout: boolean = false;
  user: UserSession | null = null;
  profile!: Profile;
  notifications: NotificationDto[] = [];

  constructor(
    private router: Router,
    private usuarioService: UserService,
    private toastService: ToastService,
    private authService: AuthService,
    private tokenStorageService: TokenStorageService,
    private notificationsService: NotificationsService
  ) {}

  ngOnInit(): void {
    const session = this.tokenStorageService.getAuth();
    this.user = session?.user || null;
    this.isCompanyDashboard = this.router.url.includes('/user/company/');

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isCompanyDashboard = event.url.includes('/user/company/');
      });
    if (this.user) {
      this.notificationsService.getUnreadNotificationsByUser().subscribe({
        next: (res) => {
          this.notifications = res.dto.notifications;
          this.usuarioService.getProfile(this.user?.idUser || '').subscribe({
            next: (data) => {
              this.profile = data.dto.user;
            },
          });
        },
        error: (err) => {
          console.error(err);
        },
      });
    }
  }

  toggleUserMenu() {
    this.userMenuOpen = !this.userMenuOpen;
    this.notificationMenuOpen = false;
  }

  toggleNotificationMenu() {
    this.userMenuOpen = false;
    this.notificationMenuOpen = !this.notificationMenuOpen;
  }

  markAllAsRead(): void {
    if (this.notifications.length === 0 || this.loadingMarkRead) {
      return;
    }

    this.loadingMarkRead = true;

    this.notificationsService
      .markAllAsRead()
      .pipe(
        finalize(() => {
          this.loadingMarkRead = false;
        })
      )
      .subscribe({
      next: () => {
        this.notifications = []; 
        this.toastService.showMessages(
          'success', 
          [],
          'Notificaciones marcadas como leídas.'
        );
      },
      error: (err) => {
        this.toastService.showMessages(
          'error', 
          [], 
          'Error al marcar notificaciones. Intenta de nuevo.'
        );
      },
    });
  }

  closeUserMenu() {
    setTimeout(() => {
      this.userMenuOpen = false;
    }, 150);
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  openConfirmLogout() {
    this.confirmLogoutOpen = true;
  }

  closeConfirmLogout() {
    this.confirmLogoutOpen = false;
  }

  confirmLogout(): void {
    this.loadingLogout = true;
    this.authService
      .logout()
      .pipe(
        handleApiError(this.toastService, MESSAGES.AUTH.LOGOUT_ERROR),
        finalize(() => {
          this.loadingLogout = false;
          this.confirmLogoutOpen = false;
        })
      )
      .subscribe({
        next: () => {
          this.router.navigate(['/']);
        },
      });
  }

  goHome() {
    if (this.router.url === '/') {
      window.location.reload();
    } else {
      this.router.navigateByUrl('/');
    }
  }

  get userImage(): string {
    const url = this.profile?.urlAvatar;
    if (!url || url.trim() === '') {
      return APP_CONSTANTS.DEFAULT_IMAGE_PROFILE;
    }
    return url;
  }

  get linkClass() {
    return this.isCompanyDashboard ? 'hover:text-white' : 'hover:text-primary';
  }

  get iconClass() {
    return this.isCompanyDashboard
      ? 'hover:text-white text-gray-400'
      : 'hover:text-primary text-gray-400';
  }

  get imageBorderClass() {
    return this.isCompanyDashboard
      ? 'hover:border-transparent'
      : 'hover:border-primary';
  }
}
